import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QRScanner = ({ onScan, loading }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      setError('');
      setIsScanning(true);

      const html5QrCode = new Html5Qrcode('qr-scanner-region');
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 300, height: 300 },
        aspectRatio: 1.0
      };

      await html5QrCode?.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          if (decodedText && !loading) {
            onScan?.(decodedText);
            stopScanner();
          }
        },
        (errorMessage) => {
          // Ignore continuous scanning errors
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Unable to access camera. Please check permissions.');
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef?.current) {
        await html5QrCodeRef?.current?.stop();
        html5QrCodeRef.current = null;
      }
      setIsScanning(false);
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <h2 className="text-xl font-bold mb-6">QR Code Scanner</h2>

      {/* Scanner Region */}
      <div className="mb-6">
        {!isScanning ? (
          <div className="bg-muted rounded-lg p-12 text-center">
            <Icon name="Camera" size={64} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-6">
              Click the button below to start scanning QR codes
            </p>
            <Button
              onClick={startScanner}
              iconName="Camera"
              disabled={loading}
            >
              Start Camera
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div id="qr-scanner-region" className="rounded-lg overflow-hidden" />
            <Button
              onClick={stopScanner}
              variant="outline"
              iconName="X"
              className="w-full"
            >
              Stop Scanner
            </Button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive rounded-lg mb-6">
          <p className="text-sm text-destructive flex items-center gap-2">
            <Icon name="AlertCircle" size={16} />
            {error}
          </p>
        </div>
      )}

      {/* Instructions */}
      <div className="p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Icon name="Info" size={18} />
          Scanning Instructions
        </h3>
        <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
          <li>Position the QR code within the scanning frame</li>
          <li>Ensure good lighting for best results</li>
          <li>Hold steady until the code is detected</li>
          <li>The scanner will automatically process valid codes</li>
        </ul>
      </div>
    </div>
  );
};

export default QRScanner;